//REACT AND REACT BASED LIBRARIES
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWRMutation from "swr/mutation";
import i18n from "../../../../../i18n";
//COMPONENTS
import Table from "@components/Table";
import { Box, Container } from "@mui/material";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import BackgroundImage from "@components/BackgroundImageCst";
import CustomRowAction from "@components/Table/components/CustomRowAction";
//CUSTOM HOOKS AND UTILS
import { useFetchDetail } from "@hooks/useFetchDetail";
import {
  downloadBlobFile,
  downloadFile,
  ServicesURL,
  uploadFile,
} from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import { PagesID } from "@utils/utilsConfigurations";
import { axiosInstance, fetcher } from "src/services/config";
import { ActionsType } from "src/interfaces/Common";
import ButtonBackPage from "@components/ButtonBackPage";
import BookIcon from "@mui/icons-material/Book";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import ModalDownloadUploadCst from "@components/ModalDownloadUploadCst";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { getBase64 } from "@pages/Contents/BadgeAndCertificates/utils/utils";
import { AxiosResponse } from "axios";
import { useAuth } from "@context/auth";
import dayjs from "dayjs";
import { useFilters } from "@context/filters";
import FormLoadingStateCst from "@components/FormLoadingStateCst";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { useFetchReasonCodes } from "@pages/Contents/Corporate/EditionsPhysical/hooks/useFetchReasonCodes";
import { Row } from "@components/Table/style";

const VirtualRegister: React.FC = () => {
  const { t } = useTranslation(["virtual-register-page"]);
  const navigate = useNavigate();
  const { id = "1", idEdition } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["corporate.virtual-register"]}`;
  const { pathname } = useLocation();
  const currentLanguage = i18n.language;

  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const [isAttendanceReport, setIsAttendanceReport] = useState<boolean | null>(
    null,
  );

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const { columns, dataTable, actionsTopToolbar, actionForRow } = useFetchTable(
    `${ServicesURL.getVirtualRegister.replace(":editionId", idEdition || "")}?corporateId=${id}&forceUpdate=${true}`, //!! (SOLO DI ESEMPIO) MODIFICARE SERVICE URL CON QUELLO INSERITO NEL FILE DI CONFIG
    PagesID["corporate.virtual-register"], //!! (SOLO DI ESEMPIO) MODIFICARE PAGE ID CON QUELLO INSERITO NEL FILE DI CONFIG
    filters[filterId],
  );

  const { dataDetail: dataEdition } = useFetchDetail(
    `${ServicesURL.getEditionsDetail}${pathname.includes("physical-editions") ? "/physical" : "/virtual"}`,
    idEdition + `?corporateId=${id}` || "",
    pathname.includes("physical-editions")
      ? PagesID["corporate.physical-editions"]
      : PagesID["corporate.virtual-editions"],
  );

  const {
    reasonCodes,
    formattedOptions,
    isLoading: isLoadingReasonCodes,
  } = useFetchReasonCodes(id, "ABSENCE");

  const renderReasonCodeCell = (cel: any, value: any, row: any) => {
    if (cel.accessorKey === "absenceReason" && formattedOptions) {
      const reasonCode = formattedOptions.find(
        (option: { value: any }) => option.value === value,
      );
      // Debug to check if values match
      //console.log("Looking for value:", value);
      //console.log("Available options:", formattedOptions);
      //console.log("Found reason code:", reasonCode);
      return reasonCode ? (
        <Row>{reasonCode.label[currentLanguage]}</Row>
      ) : (
        <Row>{value || "-"}</Row>
      );
    }
    return undefined; // Return undefined to let the default renderer handle other columns
  };

  useEffect(() => {
    if (
      dataEdition &&
      Object.keys(dataEdition).length > 0 &&
      isAttendanceReport === null
    ) {
      //getParticipantFileData();
      setIsAttendanceReport(true);
    }
  }, [dataEdition]);

  const getParticipantFileData = () => {
    if (
      pathname.includes("virtual-editions") &&
      dataEdition?.videoconference_tool === "TEAMS_DIG"
    ) {
      const urlDownloadPaticipants: any = ServicesURL["attendancePresence"];
      axiosInstance
        .get(
          `${urlDownloadPaticipants}?corporateId=${id}&editionId=${idEdition}`,
        )
        .then((res: AxiosResponse) => {
          setIsAttendanceReport(true);
        })
        .catch((error) => setIsAttendanceReport(false));
    } else {
      setIsAttendanceReport(true);
    }
  };
  //FUNZIONE NECESSARIA PER APPORTARE MODIFICHE AI CAMPI DELLE AZIONI DI RIGA
  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        default:
          return action;
      }
    });
  }, [actionForRow]);

  //FUNZIONE NECESSARIA PER APPORTARE MODIFICHE AI CAMPI DELLE AZIONI DI RIGA MA IN BASE AL VALORE DEI CAMPI (ROW)
  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const filterOptions = React.useMemo(() => {
    const options: { [key: string]: any } = {};

    if (formattedOptions && formattedOptions.length > 0) {
      // This format should match what the Table component expects
      options["absenceReason"] = formattedOptions;
    }

    return options;
  }, [formattedOptions]);

  // In VirtualRegister/index.tsx
  // This will prepare the columns configuration with our dynamic options
  const prepareColumnsWithDynamicFilters = React.useMemo(() => {
    // Make a copy of the columns from the table config
    const updatedColumns = columns ? [...columns] : [];

    // Find the absenceReason column and add options to it
    if (updatedColumns && formattedOptions && formattedOptions.length > 0) {
      const absenceReasonColumn = updatedColumns.find(
        (column: any) => column.accessorKey === "absenceReason",
      );

      if (absenceReasonColumn) {
        // Add the dynamically fetched options to the column config
        absenceReasonColumn.optionsKey = formattedOptions;
      }
    }

    return updatedColumns;
  }, [columns, formattedOptions]);

  // If you need to modify the columns to use these options:
  const updatedColumns = useMemo(() => {
    if (!columns || !formattedOptions || formattedOptions.length === 0) {
      return columns;
    }

    return columns.map((column) => {
      if (column.accessorKey === "absenceReason") {
        //console.log("Modifying absenceReason column with options:", formattedOptions);
        return {
          ...column,
          // Add options directly to the column configuration
          options: formattedOptions.map(
            (option: { value: any; label: { [x: string]: any } }) => ({
              value: option.value,
              label: option.label[currentLanguage], // Use the label for current language
            }),
          ),
          type: "select",
          // This is for display rendering
          Cell: ({ cell }: { cell: any }) => {
            const value = cell.getValue();
            const option = formattedOptions.find(
              (opt: { value: any }) => opt.value === value,
            );
            return option ? (
              <Row>{option.label[currentLanguage]}</Row>
            ) : (
              <Row>{value || "-"}</Row>
            );
          },
        };
      }
      return column;
    });
  }, [columns, formattedOptions, currentLanguage]);

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    let toolbar =
      actionsTopToolbar?.map((action: ActionsType) => {
        switch (action.type) {
          case "upload": {
            const actionModal: ActionUploadDownloadIterface = {
              ...action,
              ...{
                types: ["upload", "download"],
                onDownload: (el: any) => {
                  downloadFile(
                    `${ServicesURL.getVirtualRegisterFile}?corporateId=${id}&editionId=${idEdition}`,
                    false,
                    "",
                  );
                },
                onUpload: async (formData: any) => {
                  const file = formData.get("file");
                  return await getBase64(formData.get("file")).then(
                    (newFile: any) => {
                      const date = dayjs().format("YYYYMMDDHHmmss");
                      const formattedParams: any = {
                        contentFile:
                          newFile?.split(",") && newFile?.split(",")[1],
                        nomeFile: `${file.name.split(".")[0]}_${date}.${file.name.split(".")[1]}`,
                        tipoFile:
                          file.type.split("/") && file.type.split("/")[1],
                      };

                      const response = uploadFile(
                        `${ServicesURL.postVirtualRegisterFile}?editionId=${idEdition}`,
                        {
                          fileZip: formattedParams,
                          corporateId: id,
                          initiativeId: null,
                        },
                        MethodType.POST,
                        "application/json",
                      );

                      return response;
                    },
                  );
                },
              },
            };
            return {
              ...action,
              disabled: !isAttendanceReport,
              getIcon: () => <BookIcon />,
              ...{ callBack: () => setOpenUpload(actionModal) },
            };
          }
          case "button":
            return {
              ...action,
              getIcon: () => <CollectionsBookmarkIcon />,
              callBack: !isAttendanceReport
                ? undefined
                : () => navigate("history-register"),
            };
          default:
            return action;
        }
      }) || [];

    //TODO: PULSANTE DA AGGIUNGERE SOLO NEL CASO DI EDIZIONI VIRTUALI
    /*if (pathname.includes("virtual-editions")) {
      toolbar.push({
        type: "button2",
        permission: "catalogue.virtualregister.write",
        labelTranslate: t("button-sync"),
        getIcon: () => <SyncIcon />,
        callBack: () => setOpenConfirmModal(true),
      } as any);
    }*/

    return toolbar;
  }, [actionsTopToolbar, pathname, isAttendanceReport, navigate, t]);

  const formatResponse = useMemo(() => {
    if (dataTable?.output) {
      return dataTable?.output.map((el: any) => {
        return {
          ...el,
          editionEnrollmentDate: el.editionEnrollmentDate * 1000, //TRANSORM FROM JAVA SECONDS TO JS MILLISECONDS
          lastProcessingDate: el.lastProcessingDate * 1000, //TRANSORM FROM JAVA SECONDS TO JS MILLISECONDS
        };
      });
    } else {
      return [];
    }
  }, [dataTable]);

  const { trigger: triggerSynchronize } = useSWRMutation(
    `${ServicesURL.attendanceReport.replace("{editionId}", idEdition || "1")}`,
    (url: string, { arg }) => {
      return fetcher<any>(`${url}`, MethodType.GET, arg);
    },
  );

  const handleConfirmModal = async () => {
    setConfirmModal({ s: StatusEnum.LOADING, message: "loading" });

    try {
      /*let response = await triggerSynchronize();
      let fileName = `prova.xls`;
      const blob = new Blob([response], { type: "application/vnd.ms-excel" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setConfirmModal({
        s: StatusEnum.OK,
        message: "",
      });*/
      await downloadBlobFile(
        `${ServicesURL.attendanceReport.replace("{editionId}", idEdition || "1")}`,
        null,
        "fileName.xlsx",
        MethodType.GET,
      );
      setConfirmModal({
        s: StatusEnum.OK,
        message: "",
      });
    } catch (error) {
      setConfirmModal(undefined);
      setOpenConfirmModal(false);
      console.error(error);
    }
  };

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-certificates.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${dataEdition && dataEdition?.edition_code ? `${dataEdition?.edition_code} - ` : ""}${t("hero-title")}`}
          description={t("hero-description")}
        />
        <ButtonBackPage customNavigate={() => navigate(-1)} />
        {!dataTable ? (
          <FormLoadingStateCst />
        ) : (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <Table
              id={PagesID["corporate.virtual-register"]}
              data={formatResponse}
              columns={updatedColumns || []}
              configurationTopToolbar={actionsToolbar}
              renderRowActions={customRenderRowActions}
              totalPages={dataTable?.metadata?.total_elements}
              modalTitle={t("hero-title")?.replace("-", "") || ""}
              enableRowActions={false}
              renderCell={renderReasonCodeCell}
              filterOptions={filterOptions}
            />
          </Box>
        )}
      </Container>

      <ModalDownloadUploadCst
        open={!!openUpload}
        setOpen={() => {
          setOpenUpload(undefined);
        }}
        title={t("modal-upload-title") || ""}
        data={openUpload}
      />

      <ModalConfirmsCst
        open={openConfirmModal}
        title={`${t("confirm-sync")}`}
        description={`${t("description-sync")}`}
        onCallBackClosed={() => {
          setConfirmModal(undefined);
          setOpenConfirmModal(false);
        }}
        onActionConfirmed={() => handleConfirmModal()}
        status={confirmModal}
      />
    </AnimationFadeIn>
  );
};

export default VirtualRegister;
